import React, { useState } from 'react';
import {
    useGetAllReservationsDetailedQuery,
    useGetAvailabilityQuery,
    useGetIndividualOwnerAssetsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import {
    Autocomplete,
    Avatar,
    Box,
    Card,
    CardContent,
    FormHelperText,
    Grid,
    InputLabel,
    ListItemAvatar,
    ListItemText,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { Status } from '../../../enums/booking';
import GenericTable from '../../../shared/GenericTable';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useNavigate } from 'react-router';
import {
    CommercialDetails,
    FormValues as Experience,
} from '../../../types/experience';
import { ExperienceType } from '../../../enums/experience';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { NavigateNext } from '@mui/icons-material';
import moment from 'moment';

const DailyVisitors = () => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();

    const { data: experiences } = useGetIndividualOwnerAssetsQuery(
        {
            url: `/owners/${loggedInUser?.company}/modules`,
            status: 'published',
        },
        { skip: !loggedInUser },
    );

    const [selectedExperience, setSelectedExperience] =
        useState<Experience | null>(null);
    const [date, setDate] = useState<Dayjs | null>(dayjs());

    const { data: bookings, isLoading } = useGetAllReservationsDetailedQuery(
        {
            module: selectedExperience?.id,
            scheduledAt: date ? format(date.toDate(), 'yyyy-MM-dd') : undefined,
            status: [Status.Approved, Status.Completed],
            bookerType: 'guest',
        },
        { skip: !selectedExperience },
    );

    const { data: availability, isFetching: availabilityLoading } =
        useGetAvailabilityQuery(
            {
                url: `/modules/commercial/${selectedExperience?.id}/get-availability`,
                body: {
                    startDate: date
                        ? format(date.toDate(), 'yyyy-MM-dd')
                        : undefined,
                    endDate: date
                        ? format(date.toDate(), 'yyyy-MM-dd')
                        : undefined,
                },
            },
            { skip: !selectedExperience },
        );

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'id',
            header: 'Booking',
            size: 10,
            accessorFn: (row) => `#${row.id}`,
        },
        {
            id: 'booker',
            header: 'Guest',
            Cell: ({ row }) => (
                <div
                    style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                    }}
                >
                    <Avatar src={row?.original?.booker.profilePicture?.src} />
                    <text>
                        <b>
                            {row?.original.booker?.firstName +
                                ' ' +
                                row?.original.booker?.lastName}
                        </b>
                        <br />
                        {row?.original.booker?.email}
                    </text>
                </div>
            ),
        },
        {
            accessorKey: 'owner.name',
            header: 'Company',
        },
        {
            accessorKey: 'startDate',
            header: 'Check-in',
            accessorFn: (row) =>
                row.startDate
                    ? moment(row.startDate).format('DD/MM/YYYY')
                    : '-',
        },
        {
            accessorKey: 'endDate',
            header: 'Checkout',
            accessorFn: (row) =>
                row.endDate ? moment(row.endDate).format('DD/MM/YYYY') : '-',
        },
        {
            id: 'workspaces',
            header: 'Workspaces',
            enableSorting: false,
            accessorFn: (row) => {
                return [
                    row.bookingInfo?.desks > 0 && 'Desk',
                    row.bookingInfo?.privateOffices > 0 && 'Private Office',
                    row.bookingInfo?.meetingRooms > 0 && 'Meeting Room',
                ]
                    .filter(Boolean)
                    .join(' + ');
            },
        },
        {
            id: 'spaces',
            header: 'Rooms',
            enableSorting: false,
            accessorFn: (row) =>
                row.spaces
                    ?.filter((space: any) => {
                        if (!date) return false;
                        return (
                            new Date(`${space.startDate}T00:00:00`) <=
                                date.toDate() &&
                            new Date(`${space.endDate}T00:00:00`) >=
                                date.toDate()
                        );
                    })
                    .map((space: any) => space.assignedName)
                    .join(', '),
        },
    ];

    return (
        <Stack sx={{ pt: '30px' }} spacing={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <div>
                    <InputLabel>Experience</InputLabel>
                    <Autocomplete
                        size='small'
                        options={experiences
                            ?.filter(
                                ({ type }: any) =>
                                    type === ExperienceType.Commercial,
                            )
                            .map((experience: Experience) => ({
                                value: experience.id,
                                label: experience.name,
                                ...experience,
                            }))}
                        renderOption={(props, option: Experience) => (
                            <li key={option.id} {...props}>
                                <ListItemAvatar>
                                    <Avatar
                                        src={option.photos[0]?.src}
                                        variant='rounded'
                                    />
                                </ListItemAvatar>
                                <ListItemText
                                    primary={option.name}
                                    secondary={`${option.streetAddress}, ${option.suburb}`}
                                />
                            </li>
                        )}
                        onChange={(_, value) => setSelectedExperience(value)}
                        value={selectedExperience}
                        renderInput={(params) => <TextField {...params} />}
                    />
                    <FormHelperText>
                        This feature only gives insights for commercial
                        experiences
                    </FormHelperText>
                </div>
                <div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <InputLabel>Date</InputLabel>
                        <DesktopDatePicker
                            value={date}
                            onChange={setDate}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                },
                            }}
                            format='DD/MM/YYYY'
                        />
                    </LocalizationProvider>
                </div>
            </Box>
            {selectedExperience && (
                <Grid container gap={2}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box
                                    sx={{
                                        py: 1,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        pr: 2,
                                        transition:
                                            'padding-right 0.3s ease-in-out',
                                        '&:hover': {
                                            pr: 1,
                                        },
                                    }}
                                    onClick={() =>
                                        navigate(
                                            `/viewExperiences/${loggedInUser?.company}/${selectedExperience.type}/${selectedExperience.id}`,
                                        )
                                    }
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            alignItems: 'center',
                                        }}
                                    >
                                        <img
                                            src={
                                                selectedExperience.photos[0]
                                                    ?.src
                                            }
                                            width={50}
                                            height={50}
                                            style={{
                                                borderRadius: 3,
                                                objectFit: 'cover',
                                            }}
                                        />
                                        <Stack spacing={0.25}>
                                            <Typography variant='subHeading'>
                                                {selectedExperience.name}
                                            </Typography>
                                            <Typography
                                                variant='body3'
                                                component='p'
                                                sx={{ display: 'block', m: 0 }}
                                            >
                                                {
                                                    selectedExperience.streetAddress
                                                }
                                                , {selectedExperience.suburb},{' '}
                                                {selectedExperience.state}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                    <NavigateNext />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                {availabilityLoading ? (
                                    <>
                                        <Skeleton variant='text' width={100} />
                                        <Skeleton
                                            height={40}
                                            width={50}
                                            variant='rounded'
                                        />
                                    </>
                                ) : (
                                    <>
                                        <Typography>Desks</Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 36,
                                                fontWeight: 'bold',
                                                lineHeight: 1,
                                            }}
                                        >
                                            {(
                                                selectedExperience?.details as CommercialDetails
                                            )?.desks - availability?.desks}
                                        </Typography>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                {availabilityLoading ? (
                                    <>
                                        <Skeleton variant='text' width={100} />
                                        <Skeleton
                                            height={40}
                                            width={50}
                                            variant='rounded'
                                        />
                                    </>
                                ) : (
                                    <>
                                        <Typography>Boardrooms</Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 36,
                                                fontWeight: 'bold',
                                                lineHeight: 1,
                                            }}
                                        >
                                            {(
                                                selectedExperience?.details as CommercialDetails
                                            )?.meetingRooms -
                                                availability?.meetingRooms}
                                        </Typography>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                {availabilityLoading ? (
                                    <>
                                        <Skeleton variant='text' width={100} />
                                        <Skeleton
                                            height={40}
                                            width={50}
                                            variant='rounded'
                                        />
                                    </>
                                ) : (
                                    <>
                                        <Typography>Offices</Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 36,
                                                fontWeight: 'bold',
                                                lineHeight: 1,
                                            }}
                                        >
                                            {(
                                                selectedExperience?.details as CommercialDetails
                                            )?.privateOffices -
                                                availability?.privateOffices}
                                        </Typography>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
            <Stack spacing={1}>
                <Typography variant='subHeading'>Reservations</Typography>
                <GenericTable
                    columns={columns}
                    tableName='Bookings'
                    data={bookings ?? []}
                    loading={isLoading}
                    enableRowSelection={false}
                    renderTopToolbar={<></>}
                    onRowClick={(row) => {
                        if (!selectedExperience) return;
                        navigate(
                            `/reservations/${selectedExperience.name}/${row.id}`,
                        );
                    }}
                />
            </Stack>
        </Stack>
    );
};

export default DailyVisitors;
